import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";
import { useInvoices, fmtMoney, fmtInvoiceDate } from "../lib/admin";

/* Adaptr Orbit — UI kit. Typed port of the prototype's ui.jsx primitives.
   Markup/classnames preserved so components.css applies unchanged. */

export function Btn({
  variant = "ghost",
  icon,
  iconRight,
  children,
  onClick,
  size,
  style,
  disabled,
}: {
  variant?: "ghost" | "live" | "dark" | "danger";
  icon?: string;
  iconRight?: string;
  children?: ReactNode;
  onClick?: () => void;
  size?: "sm";
  style?: CSSProperties;
  disabled?: boolean;
}) {
  return (
    <button className={`btn ${variant} ${size === "sm" ? "sm" : ""}`} onClick={onClick} style={style} disabled={disabled}>
      {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} strokeWidth={2} />}
      {children && <span>{children}</span>}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 13 : 15} strokeWidth={2} />}
    </button>
  );
}

export function Badge({
  tone = "neutral",
  children,
  icon,
}: {
  tone?: "neutral" | "live" | "violet" | "amber" | "danger";
  children?: ReactNode;
  icon?: string;
}) {
  return (
    <span className={`badge ${tone}`}>
      {icon && <Icon name={icon} size={11} strokeWidth={2.4} />}
      {children}
    </span>
  );
}

export function Avatar({ initials, size = 34 }: { initials: string; size?: number }) {
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </span>
  );
}

export function Meter({ pct, tone = "live", height = 8 }: { pct: number; tone?: string; height?: number }) {
  return (
    <div className="meter" style={{ height }}>
      <i className={tone} style={{ width: Math.min(100, pct) + "%" }} />
    </div>
  );
}

export function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      className={`toggle ${on ? "on" : ""}`}
      onClick={() => onChange?.(!on)}
      role="switch"
      aria-checked={on}
    >
      <span className="knob" />
    </button>
  );
}

export function Seg({
  options,
  value,
  onChange,
  full,
}: {
  options: string[];
  value: string;
  onChange?: (v: string) => void;
  full?: boolean;
}) {
  return (
    <div className={`seg ${full ? "full" : ""}`}>
      {options.map((o) => (
        <button key={o} aria-pressed={value === o} onClick={() => onChange?.(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function Card({
  title,
  icon,
  iconColor,
  count,
  action,
  children,
  pad = true,
  className,
}: {
  title?: ReactNode;
  icon?: string;
  iconColor?: string;
  count?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  pad?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${className || ""}`}>
      {title && (
        <header className="card-h">
          {icon && <Icon name={icon} size={16} color={iconColor || "var(--fg-3)"} />}
          <span className="card-t">{title}</span>
          {count != null && <span className="card-n">{count}</span>}
          {action}
        </header>
      )}
      <div className={pad ? "card-b" : ""}>{children}</div>
    </section>
  );
}

export function StatTile({
  icon,
  label,
  value,
  sub,
  tone = "neutral",
}: {
  icon: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "neutral" | "live";
}) {
  return (
    <div className={`stat ${tone}`}>
      <span className="stat-ic">
        <Icon name={icon} size={17} strokeWidth={2} />
      </span>
      <div className="stat-v">{value}</div>
      <div className="stat-l">{label}</div>
      {sub && <div className="stat-s">{sub}</div>}
    </div>
  );
}

export function PageHead({
  title,
  sub,
  chip,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  chip?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div className="ph-main">
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {chip}
      {actions && <div className="ph-actions">{actions}</div>}
    </div>
  );
}

export function Field({ label, children, action }: { label: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="field">
      <div className="field-l">
        {label}
        {action}
      </div>
      <div className="field-v">{children}</div>
    </div>
  );
}

export function Tag({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) {
  return (
    <span className="tag-chip">
      {children}
      {onRemove && (
        <button onClick={onRemove} aria-label="remove">
          <Icon name="x" size={12} strokeWidth={2.4} />
        </button>
      )}
    </span>
  );
}

export function Empty({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <div className="empty">
      <Icon name={icon} size={22} />
      <span>{children}</span>
    </div>
  );
}

/** Shimmer placeholder shown while live operator data loads. */
export function Skeleton({ w = "100%", h = 14, r = 6, style }: { w?: number | string; h?: number | string; r?: number; style?: CSSProperties }) {
  return <span className="gskel" style={{ width: w, height: h, borderRadius: r, display: "block", ...style }} />;
}

/** Honest placeholder for a section whose data source isn't built yet. */
export function NeedsData({ icon = "alert-triangle", children }: { icon?: string; children: ReactNode }) {
  return (
    <div className="needs-data">
      <Icon name={icon} size={20} />
      <span>{children}</span>
    </div>
  );
}

export function AIChip({ children = "Drafted by Claude" }: { children?: ReactNode }) {
  return (
    <span className="ai-chip">
      <Icon name="sparkles" size={12} strokeWidth={2} />
      {children}
    </span>
  );
}

/* Invoice list — operator view of a customer's Autumn/Stripe invoices.
   Pass { user } or { org }; handles loading / not-configured / empty states. */
export function InvoiceList({ query }: { query: { org?: string; user?: string } }) {
  const { invoices, configured } = useInvoices(query);
  if (invoices === null) {
    return <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={18} />)}</div>;
  }
  if (!configured) return <div className="needs-data" style={{ margin: 14 }}><Icon name="receipt" size={18} /><span>Autumn billing isn’t configured (no <code>AUTUMN_SECRET_KEY</code>) — invoices unavailable.</span></div>;
  if (invoices.length === 0) return <div className="gt-empty">No invoices on file.</div>;
  return (
    <div className="mini-charges">
      {invoices.map((inv) => (
        <div className="mc-row" key={inv.id}>
          <span className="gt-mut">{fmtInvoiceDate(inv.createdAt)}</span>
          <span className="mc-note">{inv.url ? <a href={inv.url} target="_blank" rel="noreferrer" className="inv-link">Invoice {inv.id.slice(-6)}</a> : `Invoice ${inv.id.slice(-6)}`}</span>
          <span className="gt-mono">{fmtMoney(inv.total, inv.currency)}</span>
          <Badge tone={inv.status === "paid" ? "live" : inv.status === "open" ? "amber" : "neutral"}>{inv.status || "—"}</Badge>
        </div>
      ))}
    </div>
  );
}
